<template>
  <v-container :class="isMobile() ? 'px-1' : ''">
    <v-form ref="form" v-model="isValid" lazy-validation>
      <v-row justify="center">
        <v-col :md="configTableSize" class="pt-0">
          <v-card>
            <v-card-title>{{ $t('clock.config') }}</v-card-title>
            <v-divider/>
            <v-container class="py-0">
              <v-row
                v-if="isConcurrentEmploymentActive"
              >
                <v-col :class="{ 'pb-0': configTableCols > 0 }">
                  <v-card flat class="grow" color="grey lighten-4">
                    <v-select
                      :value="selectedAssignment"
                      :items="assignments"
                      :item-text="assignmentText"
                      :disabled="disabled || assignments.length <= 1"
                      :prepend-icon="isMobile() ? null : 'account_box'"
                      item-value="id"
                      return-object
                      outlined
                      hide-details="auto"
                      :label="$t('clock.assignment')"
                      class="pa-4"
                      @change="setSelectedAssignment"
                    />
                  </v-card>
                  <v-divider/>
                </v-col>
              </v-row>
              <v-row
                v-if="isSelectableTimeTypeFeatureActive && validAttendanceTimeTypes.length > 1"
              >
                <v-col :class="{ 'pb-0': configTableCols > 0 }">
                  <v-card flat class="grow" color="grey lighten-4">
                    <v-select
                      :value="selectedTimeType"
                      :items="validAttendanceTimeTypes"
                      item-text="externalName"
                      :disabled="disabled"
                      :prepend-icon="isMobile() ? null : 'query_builder'"
                      item-value="id"
                      return-object
                      outlined
                      hide-details="auto"
                      :label="$t('clock.timeType')"
                      class="pa-4"
                      @change="setSelectedTimeType"
                    />
                  </v-card>
                  <v-divider/>
                </v-col>
              </v-row>
              <v-row v-if="configTableCols > 0" class="text-center">
                <v-col>
                  <v-list color="grey lighten-4" class="pa-4 fill-height">
                    <v-divider/>

                    <v-row>
                      <v-col
                        v-for="n in configTableCols"
                        :key="n"
                        :md="12 / configTableCols"
                        cols="12"
                        class="py-0"
                        sm="12"
                      >
                        <template v-for="field in getApplicableCustomFields(n)">
                          <v-item-group :key="field.id">
                            <v-item>
                              <v-row v-if="isFlag(field.type)" class="px-3">
                                <v-card-title
                                  :class="{ 'text--disabled': disabled }"
                                  class="body-1"
                                >
                                  {{ field.name }}
                                </v-card-title>
                                <v-spacer/>
                                <v-divider vertical/>
                                <v-card flat color="grey lighten-3" class="px-4">
                                  <v-checkbox
                                    v-model="field.value"
                                    :false-value="field.falseValue"
                                    :true-value="field.trueValue"
                                    :disabled="disabled"
                                    ripple
                                    color="primary"
                                    class="pl-2"
                                    @change="field.changeCallbackFunction($event, field)"
                                  />
                                </v-card>
                              </v-row>

                              <v-row v-else-if="isSelect(field.type)" class="pa-0">
                                <v-col>
                                  <v-select
                                    v-model="field.selected"
                                    :disabled="disabled"
                                    :items="field.options"
                                    :loading="field.loading"
                                    :label="`${field.name} *`"
                                    :placeholder="field.description || ' '"
                                    :rules="[rules.required]"
                                    item-text="name"
                                    item-value="id"
                                    hide-details="auto"
                                    outlined
                                    clearable
                                    validate-on-blur
                                    @change="field.changeCallbackFunction($event, field.id)"
                                  />
                                </v-col>
                              </v-row>

                              <v-row v-else class="pa-0">
                                <v-col>
                                  <v-combobox
                                    v-model="field.selected"
                                    :disabled="disabled"
                                    :items="field.options"
                                    :filter="filter"
                                    :search-input.sync="field.searchString"
                                    :loading="field.loading"
                                    :label="`${field.name} *`"
                                    :placeholder="field.description || ' '"
                                    :rules="[rules.required]"
                                    item-text="name"
                                    item-value="id"
                                    outlined
                                    hide-details="auto"
                                    clearable
                                    validate-on-blur
                                    @change="field.changeCallbackFunction($event, field.id)"
                                  />
                                </v-col>
                              </v-row>
                            </v-item>
                            <v-divider :key="'div-' + field.id"/>
                          </v-item-group>
                        </template>
                      </v-col>
                    </v-row>
                  </v-list>
                </v-col>
              </v-row>
            </v-container>
            <v-container
              v-if="noShiftDetails"
              fluid
              class="text-center"
            >
              <span class="text--disabled">{{ $t('clock.noAssignments') }}</span>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import merge from 'lodash/merge';
import some from 'lodash/some';

import constants from '@/constants';
import isMobile from '@/mixins/displayAdaptable';
import validatable from '@/mixins/validatable';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import hasAssignmentFeatures from '@/mixins/clocking/hasAssignmentFeatures';

export default {
  name: 'TimePairConfig',
  mixins: [hasAppDialog, isMobile, validatable, hasTimePairFeatures, hasAssignmentFeatures],

  props: {
    fullWidth: Boolean,
    disabled: Boolean,
    timePair: Object,
    assignments: Array,
    assignmentText: Function,
  },

  data() {
    return {
      selectedAssignment: {},
      selectedTimeType: {},
      customFieldsClone: [],
      timePairConfig: [],
    };
  },

  computed: {
    noShiftDetails() {
      return !this.isConcurrentEmploymentActive
        && !(this.isSelectableTimeTypeFeatureActive && this.validAttendanceTimeTypes.length > 1)
        && this.configTableCols === 0;
    },

    validAttendanceTimeTypes() {
      return _.sortBy(this.selectedAssignment.timeTypeProfile.availableTimeTypes.filter(
        (tt) => tt.timeTypeCategory === constants.TimeType.Category.ATTENDANCE,
      ), 'sequence');
    },

    configTableSize() {
      if (this.fullWidth) return 12;
      switch (this.$vuetify.breakpoint.name) {
        case 'xl': {
          return 6;
        }
        case 'lg': {
          return 8;
        }
        default: {
          return this.configTableCols > 1 ? 12 : 6;
        }
      }
    },

    configTableCols() {
      if (!this.isCustomFieldsFeatureActive || this.selectedAssignment.timePairCustomFields.length === 0) return 0;
      if (this.isMobile() || this.selectedAssignment.timePairCustomFields.length <= 2) return 1;
      return 2;
    },
  },

  watch: {
    timePair() {
      this.setSelectedAssignment();
      this.setSelectedTimeType();
    },

    assignments() {
      this.setSelectedAssignment();
      this.setSelectedTimeType();
    },
  },

  methods: {
    ...mapActions(['fetchCustomFieldObjects']),

    getApplicableCustomFields(colNum) {
      return this.customFieldsClone.filter((it, i) => this.insertCustomField(i, colNum));
    },

    insertCustomField(index, colNum) {
      return ((index % this.configTableCols) === (colNum - 1));
    },

    setSelectedAssignment(assignment) {
      if (assignment) {
        this.selectedAssignment = assignment;
      } else if (!isEmpty(this.timePair) && !isEmpty(this.timePair.employeeAssignment)
        && some(this.assignments, (item) => item.id === this.timePair.employeeAssignment.id)) {
        this.selectedAssignment = this.timePair.employeeAssignment;
      } else if (!isEmpty(this.assignments)) {
        this.selectedAssignment = this.assignments[0];
      } else {
        this.selectedAssignment = {};
      }

      if (!isEmpty(this.selectedAssignment)) this.setCustomFieldCallbacks();
      this.$emit('update-employee-assignment', this.selectedAssignment);
    },

    setSelectedTimeType(timeType) {
      if (timeType) {
        this.selectedTimeType = timeType;
      } else if (!isEmpty(this.timePair) && !isEmpty(this.timePair.timeType)) {
        this.selectedTimeType = this.timePair.timeType;
      } else {
        this.selectedTimeType = this.selectedAssignment.timeTypeProfile.mainAttendanceTimeType;
      }

      this.$emit('update-pair-time-type', this.selectedTimeType);
    },

    isFlag(fieldType) {
      return fieldType === constants.TimePairCustomField.Type.FLAG;
    },

    isSelect(fieldType) {
      return fieldType === constants.TimePairCustomField.Type.SELECT;
    },

    setCustomFieldCallbacks() {
      this.timePairConfig = [];
      this.customFieldsClone = [];

      if (!this.isCustomFieldsFeatureActive) {
        return;
      }

      forEach(this.selectedAssignment.timePairCustomFields, (field) => {
        const newField = cloneDeep(field);

        switch (newField.type) {
          case constants.TimePairCustomField.Type.FLAG: {
            const properties = this.getFlagFieldProperties(newField.id);
            merge(newField, properties);
            this.getFlagCustomFieldValues(newField);
            break;
          }
          case constants.TimePairCustomField.Type.SELECT: {
            const properties = this.getSelectFieldProperties(newField.id);
            merge(newField, properties);
            this.getSelectionCustomFieldValues(newField);
            break;
          }
          case constants.TimePairCustomField.Type.COMBOBOX: {
            const properties = this.getComboBoxFieldProperties(newField.id);
            merge(newField, properties);
            this.getSelectionCustomFieldValues(newField);
            break;
          }
          default: {
            return false;
          }
        }

        this.customFieldsClone.push(newField);
        return true;
      });
    },

    getFlagFieldProperties(fieldId) {
      const props = {
        trueValue: true,
        falseValue: false,
        value: this.timePair ? this.getCustomFieldValue(this.timePair.timePairCustomFieldValues,
          fieldId) : null,
      };

      const functionProps = this.getCallbackFunctions(
        this.flagChange,
        null,
        this.flagCorrectionValid,
        null,
      );

      return merge(props, functionProps);
    },

    getSelectFieldProperties(fieldId) {
      const props = {
        loading: false,
        options: [],
        selected: this.timePair ? this.getCustomFieldValue(this.timePair.timePairCustomFieldValues,
          fieldId) : null,
      };

      const functionProps = this.getCallbackFunctions(
        this.selectionChange,
        this.selectionValid,
        this.selectionCorrectionValid,
        null,
      );

      return merge(props, functionProps);
    },

    getComboBoxFieldProperties(fieldId) {
      const props = {
        loading: false,
        options: [],
        selected: this.timePair ? this.getCustomFieldValue(this.timePair.timePairCustomFieldValues,
          fieldId) : null,
        searchString: '',
      };

      const functionProps = this.getCallbackFunctions(
        this.selectionChange,
        this.selectionValid,
        this.selectionCorrectionValid,
        this.comboBoxSearch,
      );

      return merge(props, functionProps);
    },

    getCallbackFunctions(changeCallbackFunction, validate, correctionValidate, updateSearchInputCallback) {
      return {
        changeCallbackFunction,
        validate,
        correctionValidate,
        updateSearchInputCallback,
      };
    },

    flagChange(event, field) {
      if (!event) event = field.falseValue;

      const index = this.getCustomFieldIndex(this.timePairConfig, field.id);
      if (index >= 0) {
        this.timePairConfig.splice(index, 1, event);
      } else {
        this.timePairConfig.push(event);
      }

      this.$emit('update-time-pair-config', this.timePairConfig);
    },

    flagCorrectionValid(timePair, fieldId) {
      const timePairValue = this.getCustomFieldValue(timePair.timePairCustomFieldValues, fieldId);
      const configValue = this.getCustomFieldValue(this.timePairConfig, fieldId);
      return configValue.id !== timePairValue.id;
    },

    selectionChange(event, fieldId, fieldType) {
      if (isString(event)) {
        if (fieldType === constants.TimePairCustomField.Type.COMBOBOX) {
          return;
        }
        event = { id: event, timePairCustomField: { id: fieldId } };
      }

      if (event == null || isString(event)) {
        event = {
          id: null,
          timePairCustomField: { id: fieldId },
        };
      }

      const index = this.getCustomFieldIndex(this.timePairConfig, fieldId);
      if (index >= 0) {
        this.timePairConfig.splice(index, 1, event);
      } else {
        this.timePairConfig.push(event);
      }

      this.$emit('update-time-pair-config', this.timePairConfig);
    },

    selectionValid(fieldId) {
      return this.getCustomFieldValue(this.timePairConfig, fieldId).id != null;
    },

    selectionCorrectionValid(timePair, fieldId) {
      const timePairValue = this.getCustomFieldValue(timePair.timePairCustomFieldValues, fieldId);
      const configValue = this.getCustomFieldValue(this.timePairConfig, fieldId);
      return configValue.id !== timePairValue.id;
    },

    async comboBoxSearch(field) {
      if (field.loading
        || (field.searchString !== null && field.searchString.length !== 0
          && field.searchString.length < constants.MINIMUM_SEARCH_LENGTH)) {
        return;
      }
      await this.searchCustomFields(field);
    },

    getFlagCustomFieldValues(field) {
      field.loading = true;
      this.fetchCustomFieldObjects({ id: field.id, searchString: '' })
      .then((response) => {
        const values = response.data;
        field.trueValue = find(values, ['name', 'true']);
        field.falseValue = find(values, ['name', 'false']);
        this.flagChange(
          field.value,
          field,
        );
      })
      .catch(() => this.showAppErrorDialog({
        message: this.$t('dialogs.error.fieldResultFail.message',
          { field: field.name }),
      }))
      .finally(() => {
        field.loading = false;
      });
    },

    getSelectionCustomFieldValues(field) {
      field.loading = true;
      this.fetchCustomFieldObjects({ id: field.id, searchString: '' })
      .then((response) => {
        field.options = response.data;
        this.selectionChange(
          field.selected,
          field.id,
          field.type,
        );
      })
      .catch(() => this.showAppErrorDialog({
        message: this.$t('dialogs.error.fieldResultFail.message',
          { field: field.name }),
      }))
      .finally(() => {
        field.loading = false;
      });
    },

    searchCustomFields(field) {
      if (field.searchString === null) {
        return;
      }
      field.loading = true;
      this.fetchCustomFieldObjects({ id: field.id, searchString: field.searchString })
      .then((response) => {
        field.options = response.data;
      })
      .catch(() => {
        this.showAppErrorDialog({
          message: this.$t('dialogs.error.fieldResultFail.message',
            { field: field.name }),
        });
      })
      .finally(() => {
        field.loading = false;
      });
    },

    getCorrectionConfigurationValid(timePair) {
      if (this.selectedAssignment.id !== timePair.employeeAssignment.id) return true;

      let result = false;
      forEach(this.customFieldsClone, (field) => {
        if (field.correctionValidate && field.correctionValidate(timePair, field.id)) {
          result = true;
          return false;
        }
        return true;
      });

      return result;
    },

    getCustomFieldValue(customFieldValues, customFieldId) {
      return find(customFieldValues, { timePairCustomField: { id: customFieldId } });
    },

    getCustomFieldIndex(customFieldValues, customFieldId) {
      return findIndex(customFieldValues, { timePairCustomField: { id: customFieldId } });
    },

    filter(item, queryText, itemText) {
      const hasValue = (val) => (val != null ? val : '');

      const text = hasValue(itemText);
      const query = hasValue(queryText);

      return text.toString()
      .toLowerCase()
      .indexOf(query.toString().toLowerCase()) > -1;
    },
  },

  created() {
    this.setSelectedAssignment();
    this.setSelectedTimeType();
  },
};
</script>
